import { useEffect } from 'react';
import { IdCard } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { de } from '../../lib/i18n/de';
import { STEPS, TOTAL_STEPS, type StepDescriptor } from './dimensions';
import { DimensionModuleCard, type ModuleDisplayStatus } from './DimensionModuleCard';
import { SegmentedProgress } from './SegmentedProgress';
import { CaseScoreCard } from './CaseScoreCard';
import { selectCompletedCount, selectStepStatus, useWizardStore } from '../../lib/store/wizard';
import { useWizardSync } from '../../lib/store/useWizardSync';
import { useAuthOrSession } from '../../lib/authOrSession';
import { Button } from '../ui/Button';

/**
 * Hub-Seite einer Tragfähigkeitsprüfung.
 *
 * Editorial-Hero (große serif Headline + italic Subtitle + Pastell-Akzente),
 * darunter segmentierter Progress, dann Modul-Liste (Triage + 6 Dimensionen)
 * mit Stagger-Animation. Tonalität: behördlich, Sie-Form, kein Marketing.
 */
export function CaseHub() {
  const params = useParams<{ id: string }>();
  const caseId = params.id ?? 'demo';
  const navigate = useNavigate();
  const ensureCase = useWizardStore((s) => s.ensureCase);
  const caseState = useWizardStore((s) => s.cases[caseId]);
  const auth = useAuthOrSession();
  // Sync läuft NUR mit echtem User (kein Mock-Sync gegen Supabase). Im Dev-
  // Mock liefert isMock=true → userId=null → useWizardSync bleibt im idle.
  const sync = useWizardSync(caseId, {
    userId: auth.isMock ? null : (auth.user?.id ?? null),
    // Berater-Hub: fachliche Bearbeitung schreibt in die Berater-Ebene.
    layer: 'berater',
  });

  useEffect(() => {
    ensureCase(caseId);
  }, [caseId, ensureCase]);

  // navigate gibt seit react-router v7 ein Promise zurück, das wir nicht
  // awaiten – daher Wrapper, der das Promise explizit verwirft.
  const go = (to: string) => {
    void navigate(to);
  };

  const completed = selectCompletedCount(caseState);

  return (
    <div className="pb-24">
      <Hero completed={completed} />

      {sync.status === 'error' && (
        <div
          role="alert"
          className="mb-6 rounded-sm border border-red-300 bg-red-50 px-4 py-3 text-red-900"
        >
          <p className="font-sans text-xs font-medium uppercase tracking-widest">
            Verschlüsselung nicht verfügbar
          </p>
          <p className="mt-1 font-serif text-sm">
            {sync.error ??
              'Bitte Browser neu laden. Sensible Eingaben werden bis dahin nicht gespeichert.'}
          </p>
        </div>
      )}

      <CaseScoreCard caseState={caseState} />

      <section className="gp-stagger" aria-label={de.hub.sectionLabel}>
        {/* Modul 00 — Stammdaten (Phase A: separater Step für Mandant-Self-
            Erfassung; Berater darf hier auch editieren). */}
        <button
          type="button"
          onClick={() => {
            go(`/case/${caseId}/wizard/stammdaten`);
          }}
          className="group relative flex w-full items-start gap-6 border-t border-stone-200 py-8 text-left transition-colors duration-300 hover:bg-stone-50/60 focus-visible:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-stone-900 md:gap-10 md:py-10"
          aria-label={`${de.mandant.stammdaten.moduleLabel} – Modul 00`}
        >
          <span
            aria-hidden="true"
            className="mt-1 inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-sm bg-stone-200 md:h-16 md:w-16"
          >
            <IdCard size={24} className="text-stone-700" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
              Modul 00
            </p>
            <h3 className="mt-1 font-serif text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
              {de.mandant.stammdaten.moduleLabel.toUpperCase()}.
            </h3>
            <p className="mt-1 font-serif text-sm italic text-stone-500">
              {de.mandant.stammdaten.moduleSubtitle}
            </p>
          </div>
        </button>

        {STEPS.map((step) => (
          <DimensionModuleCard
            key={step.id}
            step={step}
            status={resolveDisplayStatus(
              step,
              caseState ? selectStepStatus(caseState, step.id) : 'untouched',
            )}
            onClick={() => {
              if (step.comingSoon) return;
              go(`/case/${caseId}/wizard/${step.id}`);
            }}
          />
        ))}
        {/* Bottom-Strich, identisches Pattern wie im V2-Hub */}
        <div className="border-t border-stone-200" />
      </section>

      {/* Cockpit-Übergang — Editorial-Stil, sichtbar nach der Modul-Liste */}
      <section className="mt-10 border-t border-stone-200 pt-8">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          Beratung
        </p>
        <div className="mt-3 flex flex-col items-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-prose">
            <h2 className="font-serif text-2xl font-bold tracking-tight text-stone-900">
              ZUM BERATER-COCKPIT.
            </h2>
            <p className="mt-1 font-serif text-sm italic text-stone-500">
              Bewertungstexte erfassen und Stellungnahme als DOCX generieren — auf Basis der
              eingegebenen Daten und Ihrer eigenen Einordnung.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => {
              go(`/case/${caseId}/cockpit`);
            }}
          >
            Cockpit öffnen →
          </Button>
        </div>
      </section>

      <p className="mt-10 max-w-prose font-serif text-sm italic text-stone-500">
        {de.hub.legalNote}
      </p>
    </div>
  );
}

function Hero({ completed }: { completed: number }) {
  // hub.title enthält ein \n für den Zeilenumbruch im Editorial-Stil.
  const [line1, line2] = de.hub.title.split('\n');
  return (
    <header className="relative pb-10 pt-6 md:pb-14 md:pt-14">
      {/* Dekorative Pastell-Blöcke (analog Gründer-Header) */}
      <div
        aria-hidden="true"
        className="absolute right-0 top-6 hidden h-28 w-28 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
      />
      <div
        aria-hidden="true"
        className="absolute right-16 top-20 hidden h-16 w-16 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-identity)', opacity: 0.32 }}
      />

      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.brand.tagline}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-7xl">
        {line1}
        <br />
        {line2}
      </h1>
      <p className="mt-3 font-serif text-base italic text-stone-500 md:mt-4 md:text-lg">
        {de.hub.subtitle}
      </p>

      <div className="mt-6 max-w-md md:mt-8">
        <SegmentedProgress total={TOTAL_STEPS} completed={completed} />
      </div>
    </header>
  );
}

function resolveDisplayStatus(
  step: StepDescriptor,
  storeStatus: ReturnType<typeof selectStepStatus>,
): ModuleDisplayStatus {
  if (step.comingSoon) return 'coming-soon';
  return storeStatus;
}
